import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckSquare, ChevronDown, ChevronRight, Search, Filter,
  CheckCircle, XCircle, Clock, AlertTriangle, BookOpen, Loader2, Download,
  Plus, Trash2, X, User, LayoutGrid, List, RefreshCw, Sparkles, Copy, ParkingCircle
} from "lucide-react";
import { toast } from "sonner";
import CentroAutocomplete from "@/components/CentroAutocomplete";

// ── Status config ─────────────────────────────────────────────────────────
const STATUS_CFG = {
  si:        { label: "Sí",         cls: "bg-green-100 text-green-700",  dot: "bg-green-500",  icon: CheckCircle },
  no:        { label: "No",         cls: "bg-red-100 text-red-700",      dot: "bg-red-500",    icon: XCircle },
  pendiente: { label: "Pendiente",  cls: "bg-amber-100 text-amber-700",  dot: "bg-amber-400",  icon: Clock },
};

const PRIORITY_COLOR = { Crítica: "bg-red-100 text-red-700", Alta: "bg-orange-100 text-orange-700", Media: "bg-amber-100 text-amber-700", Baja: "bg-gray-100 text-gray-600" };

// R12 (criterios 2026-10-11) · traspaso contrato -> implantación. Debe
// reflejar EXACTAMENTE HANDOFF_TRANSITIONS de backend/routers/checklists.py
// — es solo para decidir qué botones mostrar; el backend sigue validando.
const HANDOFF_LABELS = {
  borrador: "Borrador", pendiente_aceptacion: "Pendiente de aceptación", aceptado: "Aceptado",
  en_implantacion: "En implantación", en_servicio: "En servicio",
  devuelto_comercial: "Devuelto para completar", cancelado: "Cancelado",
};
const HANDOFF_NEXT = {
  borrador: ["pendiente_aceptacion"],
  pendiente_aceptacion: ["aceptado", "devuelto_comercial"],
  devuelto_comercial: ["pendiente_aceptacion"],
  aceptado: ["en_implantacion"],
  en_implantacion: ["en_servicio"],
  en_servicio: [], cancelado: [],
};
const HANDOFF_COLOR = {
  borrador: "bg-gray-100 text-gray-600", pendiente_aceptacion: "bg-amber-100 text-amber-700",
  aceptado: "bg-blue-100 text-blue-700", en_implantacion: "bg-indigo-100 text-indigo-700",
  en_servicio: "bg-green-100 text-green-700", devuelto_comercial: "bg-red-100 text-red-700",
  cancelado: "bg-gray-200 text-gray-500",
};

const newItemTemplate = () => ({ _key: Math.random().toString(36).slice(2), concepto: "", contenido_minimo: "", pendiente_de: "", status: "pendiente" });
const newSectionTemplate = () => ({ _key: Math.random().toString(36).slice(2), capitulo: "", nombre: "", items: [newItemTemplate()] });

function NewChecklistModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ titulo: "", municipio: "", contrato: "", descripcion: "", centro_cb: "", centro_nombre: "", fecha_firma_contrato: "", fecha_inicio_previsto: "", fecha_puesta_en_servicio: "" });
  const [sections, setSections] = useState([newSectionTemplate()]);
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addSection = () => setSections(s => [...s, newSectionTemplate()]);
  const removeSection = (key) => setSections(s => s.filter(x => x._key !== key));
  const updateSection = (key, field, val) => setSections(s => s.map(x => x._key === key ? { ...x, [field]: val } : x));

  const addItem = (secKey) => setSections(s => s.map(x => x._key === secKey ? { ...x, items: [...x.items, newItemTemplate()] } : x));
  const removeItem = (secKey, itemKey) => setSections(s => s.map(x => x._key === secKey ? { ...x, items: x.items.filter(i => i._key !== itemKey) } : x));
  const updateItem = (secKey, itemKey, field, val) => setSections(s => s.map(x => x._key === secKey ? { ...x, items: x.items.map(i => i._key === itemKey ? { ...i, [field]: val } : i) } : x));

  const handleSave = async () => {
    if (!form.titulo || !form.municipio || !form.contrato) { toast.error("Rellena Título, Municipio y Contrato"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sections: sections.map(sec => ({
          capitulo: sec.capitulo,
          nombre: sec.nombre,
          items: sec.items.filter(i => i.concepto.trim()).map(i => ({
            concepto: i.concepto,
            contenido_minimo: i.contenido_minimo || null,
            pendiente_de: i.pendiente_de || null,
            status: "pendiente"
          }))
        })).filter(s => s.nombre.trim())
      };
      await axios.post(`${API}/checklists`, payload);
      toast.success("Checklist creado correctamente");
      onCreated();
    } catch (e) { toast.error("Error al crear checklist"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">Nuevo Checklist</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Título *</label>
              <input value={form.titulo} onChange={e => setField("titulo", e.target.value)} placeholder="Ej: Plataforma TIC — Fase 1"
                data-testid="checklist-titulo-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Municipio *</label>
              <input value={form.municipio} onChange={e => setField("municipio", e.target.value)} placeholder="Ej: Santa Eulàlia"
                data-testid="checklist-municipio-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Contrato *</label>
              <input value={form.contrato} onChange={e => setField("contrato", e.target.value)} placeholder="Ej: PPTP 2026"
                data-testid="checklist-contrato-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Centro (CeBe SAP)</label>
              <CentroAutocomplete value={form.centro_cb ? `${form.centro_cb} — ${form.centro_nombre}` : ""}
                testId="checklist-centro-autocomplete"
                onSelect={(c) => { setField("centro_cb", c?.codigo || ""); setField("centro_nombre", c?.descripcion || ""); }} />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Descripción</label>
              <input value={form.descripcion} onChange={e => setField("descripcion", e.target.value)} placeholder="Descripción opcional"
                data-testid="checklist-descripcion-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            {/* Iter313 · O06 — plantilla generalizada desde ORA Zaragoza:
                3 hitos distintos en vez de una "fecha" genérica. */}
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Firma del contrato</label>
              <input type="date" value={form.fecha_firma_contrato} onChange={e => setField("fecha_firma_contrato", e.target.value)}
                data-testid="checklist-fecha-firma-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Inicio previsto implantación</label>
              <input type="date" value={form.fecha_inicio_previsto} onChange={e => setField("fecha_inicio_previsto", e.target.value)}
                data-testid="checklist-fecha-inicio-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Puesta en servicio (real)</label>
              <input type="date" value={form.fecha_puesta_en_servicio} onChange={e => setField("fecha_puesta_en_servicio", e.target.value)}
                data-testid="checklist-fecha-servicio-input"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
            </div>
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Secciones e Ítems</span>
              <button onClick={addSection} className="flex items-center gap-1 text-xs text-[#007934] font-semibold hover:underline">
                <Plus size={12} /> Añadir sección
              </button>
            </div>
            <div className="space-y-3">
              {sections.map((sec, si) => (
                <div key={sec._key} className="border border-gray-200 rounded-xl overflow-hidden">
                  {/* Section header */}
                  <div className="flex items-center gap-2 bg-gray-50 px-3 py-2">
                    <input value={sec.capitulo} onChange={e => updateSection(sec._key, "capitulo", e.target.value)}
                      placeholder="Capítulo (ej: PPTP 14.1)" className="w-32 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#007934]" />
                    <input value={sec.nombre} onChange={e => updateSection(sec._key, "nombre", e.target.value)}
                      placeholder="Nombre de la sección *" className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#007934]" />
                    {sections.length > 1 && (
                      <button onClick={() => removeSection(sec._key)} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                    )}
                  </div>
                  {/* Items */}
                  <div className="divide-y divide-gray-50">
                    {sec.items.map((item) => (
                      <div key={item._key} className="flex items-center gap-2 px-3 py-2">
                        <input value={item.concepto} onChange={e => updateItem(sec._key, item._key, "concepto", e.target.value)}
                          placeholder="Concepto / descripción del ítem *" className="flex-1 border border-gray-100 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#007934]" />
                        <input value={item.pendiente_de} onChange={e => updateItem(sec._key, item._key, "pendiente_de", e.target.value)}
                          placeholder="Pendiente de..." className="w-36 border border-gray-100 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#007934]" />
                        {sec.items.length > 1 && (
                          <button onClick={() => removeItem(sec._key, item._key)} className="text-red-300 hover:text-red-500"><Trash2 size={12} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="px-3 pb-2">
                    <button onClick={() => addItem(sec._key)} className="text-xs text-[#007934] hover:underline flex items-center gap-1">
                      <Plus size={11} /> Añadir ítem
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-6 py-4 border-t">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="save-new-checklist-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {saving ? "Guardando..." : "Crear Checklist"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Kanban view ─────────────────────────────────────────────────────────────
const STATUS_CYCLE = { pendiente: "si", si: "no", no: "pendiente" };
const STATUS_COLORS = {
  si:        { bg: "bg-green-500",  ring: "ring-green-200",  txt: "text-green-700",  label: "Sí" },
  no:        { bg: "bg-red-400",    ring: "ring-red-200",    txt: "text-red-600",    label: "N/A" },
  pendiente: { bg: "bg-amber-400",  ring: "ring-amber-200",  txt: "text-amber-700",  label: "Pdte." },
};

const OWNER_COLORS = [
  "bg-[#007934] text-white", "bg-blue-600 text-white", "bg-purple-600 text-white",
  "bg-rose-600 text-white",  "bg-orange-500 text-white","bg-teal-600 text-white",
  "bg-indigo-600 text-white","bg-pink-600 text-white",
];

function ownerInitials(name) {
  if (!name || name === "__none__") return "?";
  return name.split(/[\s/+-]+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");
}

function KanbanCard({ item, groupColor, onStatusChange, selected, onToggleSelect }) {
  const cfg = STATUS_COLORS[item.status] || STATUS_COLORS.pendiente;
  const [updating, setUpdating] = useState(false);

  const cycleStatus = async (e) => {
    e.stopPropagation();
    if (!item.checklist_id || !item.id) return;
    const next = STATUS_CYCLE[item.status] || "pendiente";
    setUpdating(true);
    try {
      await axios.put(`${API}/checklists/${item.checklist_id}/items/${item.id}`, { status: next });
      onStatusChange(item.id, next);
    } catch { toast.error("No se pudo actualizar"); }
    finally { setUpdating(false); }
  };

  return (
    <div className={`bg-white rounded-xl border p-3 shadow-sm hover:shadow-md transition-all group ${selected ? "border-[#007934] ring-1 ring-[#007934]/30" : "border-gray-100 hover:border-gray-200"}`}
      data-testid={`kanban-card-${item.id}`}>
      {/* Top: checkbox (G08 selección múltiple) + status dot + concept */}
      <div className="flex items-start gap-2 mb-2">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelect(item)}
          onClick={e => e.stopPropagation()}
          className="mt-0.5 flex-shrink-0 w-3.5 h-3.5 accent-[#007934] cursor-pointer"
          data-testid={`kanban-card-checkbox-${item.id}`}
          aria-label={`Seleccionar ítem: ${item.concepto}`}
        />
        <button
          onClick={cycleStatus}
          disabled={updating}
          title={`Estado: ${cfg.label} — click para cambiar`}
          className={`mt-0.5 flex-shrink-0 w-3 h-3 rounded-full ${cfg.bg} ring-2 ${cfg.ring} transition-all hover:scale-125 ${updating ? "opacity-50" : "cursor-pointer"}`}
          data-testid={`status-dot-${item.id}`}
        />
        <p className="text-xs font-medium text-gray-800 leading-snug line-clamp-3 flex-1">{item.concepto}</p>
      </div>

      {/* Checklist + section */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[10px] text-gray-400 truncate flex-1 max-w-[150px]" title={item.checklist_titulo}>
          {item.checklist_titulo?.split("—")[0]?.trim() || item.checklist_titulo}
        </span>
        {item.section_nombre && (
          <span className="text-[9px] bg-gray-100 text-gray-500 px-1 rounded truncate max-w-[80px]">{item.section_nombre}</span>
        )}
      </div>

      {/* Bottom row */}
      <div className="flex items-center justify-between gap-1 mt-1">
        {item.pendiente_de ? (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${groupColor || "bg-blue-50 text-blue-600"} truncate max-w-[110px]`}>
            {item.pendiente_de}
          </span>
        ) : <span />}
        {item.fecha_compromiso && (
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5 flex-shrink-0">
            <Clock size={8} />{item.fecha_compromiso}
          </span>
        )}
      </div>
    </div>
  );
}

function ChecklistKanban() {
  const [allItems, setAllItems] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCl, setFilterCl] = useState("all");   // checklist filter
  const [groupBy, setGroupBy] = useState("pendiente_de"); // "pendiente_de" | "owner"
  const [search, setSearch] = useState("");
  // Iter315 · G08 — selección múltiple para acciones masivas
  const [selected, setSelected] = useState({}); // { [item.id]: {id, checklist_id} }
  const [bulkOwner, setBulkOwner] = useState("");
  const [applyingBulk, setApplyingBulk] = useState(false);

  const toggleSelect = (item) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = { id: item.id, checklist_id: item.checklist_id };
      return next;
    });
  };
  const clearSelection = () => setSelected({});
  const selectedList = Object.values(selected);

  const applyBulk = async (patch) => {
    if (selectedList.length === 0) return;
    setApplyingBulk(true);
    try {
      await axios.put(`${API}/checklists/items/bulk`, {
        items: selectedList.map(s => ({ checklist_id: s.checklist_id, item_id: s.id })),
        ...patch,
      });
      toast.success(`${selectedList.length} ítem${selectedList.length === 1 ? "" : "s"} actualizado${selectedList.length === 1 ? "" : "s"}`);
      clearSelection();
      setBulkOwner("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo aplicar la acción masiva"); }
    finally { setApplyingBulk(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [itemsRes, clRes] = await Promise.all([
        axios.get(`${API}/checklists-kanban-items`),
        axios.get(`${API}/checklists`),
      ]);
      setAllItems(itemsRes.data);
      setChecklists(clRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleStatusChange = (itemId, newStatus) => {
    setAllItems(prev => prev.map(i => i.id === itemId ? { ...i, status: newStatus } : i));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <Loader2 size={24} className="animate-spin text-[#007934]" />
    </div>
  );

  // Apply filters
  const filtered = allItems.filter(item => {
    if (filterCl !== "all" && item.checklist_id !== filterCl) return false;
    if (filterStatus !== "all" && item.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [item.concepto, item.checklist_titulo, item.pendiente_de, item.owner, item.section_nombre]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Build columns by groupBy field
  const getGroupKey = (item) => {
    if (groupBy === "owner") return item.owner || "__none__";
    // pendiente_de: normalize composite values like "DEV/TIC" → individual entries
    return item.pendiente_de || "__none__";
  };

  const allKeys = [...new Set(filtered.map(getGroupKey))].sort((a, b) => {
    if (a === "__none__") return 1;
    if (b === "__none__") return -1;
    return a.localeCompare(b);
  });

  const colMap = {};
  allKeys.forEach(k => { colMap[k] = filtered.filter(i => getGroupKey(i) === k); });

  // Stats
  const total    = filtered.length;
  const done     = filtered.filter(i => i.status === "si").length;
  const pending  = filtered.filter(i => i.status === "pendiente").length;
  const pctDone  = total ? Math.round(done / total * 100) : 0;

  return (
    <div className="space-y-4" data-testid="kanban-view">
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar ítem, responsable…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
            data-testid="kanban-search" />
        </div>

        {/* Checklist filter */}
        <select value={filterCl} onChange={e => setFilterCl(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] bg-white max-w-[220px]"
          data-testid="kanban-checklist-filter">
          <option value="all">Todos los checklists</option>
          {checklists.map(cl => (
            <option key={cl.id} value={cl.id}>{cl.titulo?.substring(0, 40)}{cl.titulo?.length > 40 ? "…" : ""}</option>
          ))}
        </select>

        {/* Group by toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setGroupBy("pendiente_de")} data-testid="group-by-pendiente"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${groupBy === "pendiente_de" ? "bg-white shadow-sm text-[#007934]" : "text-gray-500 hover:text-gray-700"}`}>
            Por equipo
          </button>
          <button onClick={() => setGroupBy("owner")} data-testid="group-by-owner"
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${groupBy === "owner" ? "bg-white shadow-sm text-[#007934]" : "text-gray-500 hover:text-gray-700"}`}>
            Por responsable
          </button>
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-1">
          {[["all","Todos"],["pendiente","Pendientes"],["si","Completados"],["no","N/A"]].map(([v,l]) => (
            <button key={v} onClick={() => setFilterStatus(v)} data-testid={`kanban-filter-${v}`}
              className={`px-2.5 py-1.5 text-xs rounded-lg font-medium transition-all ${filterStatus === v ? "bg-[#007934] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-[#007934]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Summary bar */}
      <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-6">
        <div className="flex-1">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span className="font-semibold text-gray-700">Progreso global</span>
            <span className="font-bold text-[#007934]">{pctDone}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#007934] rounded-full transition-all duration-500" style={{ width: `${pctDone}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs flex-shrink-0">
          <span className="text-green-600 font-bold">{done} completados</span>
          <span className="text-amber-600 font-bold">{pending} pendientes</span>
          <span className="text-gray-400">{total} total · {allKeys.length} columnas</span>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-[#007934] transition-colors" title="Actualizar">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Empty state */}
      {allKeys.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
          <User size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm font-medium">No hay ítems con el filtro actual</p>
          <p className="text-gray-300 text-xs mt-1">
            {groupBy === "owner" ? "Asigna responsables (owner) en la vista de lista" : "Prueba cambiar el filtro de estado"}
          </p>
        </div>
      )}

      {/* Kanban columns */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "60vh" }}>
        {allKeys.map((key, colIdx) => {
          const colItems = colMap[key];
          const isUnassigned = key === "__none__";
          const colDone = colItems.filter(i => i.status === "si").length;
          const colPct = colItems.length ? Math.round(colDone / colItems.length * 100) : 0;
          const colorClass = isUnassigned ? "bg-gray-500 text-white" : OWNER_COLORS[colIdx % OWNER_COLORS.length];
          const pendingBadgeColor = isUnassigned
            ? "bg-gray-50 text-gray-500"
            : OWNER_COLORS[colIdx % OWNER_COLORS.length].includes("007934")
              ? "bg-[#007934]/10 text-[#007934]"
              : "bg-blue-50 text-blue-600";

          return (
            <div key={key} className="flex-shrink-0 w-[272px] flex flex-col gap-2" data-testid={`kanban-col-${key}`}>
              {/* Column header */}
              <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold ${colorClass}`}>
                    {ownerInitials(key)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold font-['Outfit'] truncate ${isUnassigned ? "text-gray-400 italic" : "text-gray-900"}`}>
                      {isUnassigned ? "Sin asignar" : key}
                    </p>
                    <p className="text-[10px] text-gray-400">{colDone}/{colItems.length} completados</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${colorClass}`}>
                    {colItems.length}
                  </span>
                </div>
                {/* Mini progress */}
                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${colPct === 100 ? "bg-green-500" : colPct >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                    style={{ width: `${colPct}%` }}
                  />
                </div>
              </div>

              {/* Cards */}
              <div className="flex flex-col gap-2 flex-1">
                {colItems.map(item => (
                  <KanbanCard
                    key={item.id}
                    item={item}
                    groupColor={pendingBadgeColor}
                    onStatusChange={handleStatusChange}
                    selected={!!selected[item.id]}
                    onToggleSelect={toggleSelect}
                  />
                ))}
                {colItems.length === 0 && (
                  <div className="border-2 border-dashed border-gray-100 rounded-xl h-12 flex items-center justify-center">
                    <p className="text-xs text-gray-300">Vacío</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Iter315 · G08 — barra de acciones masivas flotante */}
      {selectedList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-3 z-50" data-testid="bulk-actions-bar">
          <span className="text-xs font-semibold">{selectedList.length} seleccionado{selectedList.length === 1 ? "" : "s"}</span>
          <div className="w-px h-5 bg-gray-700" />
          <button onClick={() => applyBulk({ status: "si" })} disabled={applyingBulk}
            className="text-xs font-semibold bg-green-600 hover:bg-green-500 rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid="bulk-mark-done-btn">
            Marcar como hechos
          </button>
          <button onClick={() => applyBulk({ status: "pendiente" })} disabled={applyingBulk}
            className="text-xs font-semibold bg-amber-600 hover:bg-amber-500 rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid="bulk-mark-pending-btn">
            Marcar como pendientes
          </button>
          <div className="flex items-center gap-1.5">
            <input value={bulkOwner} onChange={e => setBulkOwner(e.target.value)} placeholder="Reasignar a…"
              className="text-xs text-gray-900 rounded-lg px-2 py-1.5 w-32" data-testid="bulk-owner-input" />
            <button onClick={() => applyBulk({ owner: bulkOwner })} disabled={applyingBulk || !bulkOwner.trim()}
              className="text-xs font-semibold bg-[#007934] hover:bg-[#006228] rounded-lg px-3 py-1.5 disabled:opacity-40" data-testid="bulk-reassign-btn">
              Aplicar
            </button>
          </div>
          <div className="w-px h-5 bg-gray-700" />
          <button onClick={clearSelection} className="text-xs text-gray-400 hover:text-white" data-testid="bulk-clear-selection-btn">
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}
function ChecklistList({ onSelect }) {
  const [checklists, setChecklists] = useState([]);
  const [unified, setUnified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);   // Iter186
  const [duplicating, setDuplicating] = useState(null);    // Iter186
  const [viewMode, setViewMode] = useState("list"); // "list" | "kanban"
  const [tipoTab, setTipoTab] = useState("todas");  // "todas" | "tic" | "arrive"
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/checklists`).then(r => setChecklists(r.data)),
      axios.get(`${API}/checklists-unified`).then(r => setUnified(r.data)),
    ]).finally(() => setLoading(false));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este checklist? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/checklists/${id}`);
      toast.success("Checklist eliminado");
      load();
    } catch { toast.error("Error al eliminar el checklist"); }
  };

  // Iter186 · Duplicar checklist existente (copia todo, resetea items a pendiente)
  const handleDuplicate = async (e, id) => {
    e.stopPropagation();
    setDuplicating(id);
    try {
      const r = await axios.post(`${API}/checklists/${id}/duplicate`);
      toast.success(`Checklist duplicado: ${r.data.titulo}`);
      load();
    } catch { toast.error("Error al duplicar el checklist"); }
    finally { setDuplicating(null); }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center h-48"><Loader2 size={24} className="animate-spin text-[#007934]" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Checklists de Implantación</h1>
          <p className="text-sm text-gray-500 mt-0.5">Seguimiento de requisitos por contrato/municipio</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setViewMode("list")} data-testid="view-mode-list"
              className={`p-1.5 rounded-md transition-all ${viewMode === "list" ? "bg-white shadow-sm text-[#007934]" : "text-gray-400 hover:text-gray-600"}`}
              title="Vista lista">
              <List size={15} />
            </button>
            <button onClick={() => setViewMode("kanban")} data-testid="view-mode-kanban"
              className={`p-1.5 rounded-md transition-all ${viewMode === "kanban" ? "bg-white shadow-sm text-[#007934]" : "text-gray-400 hover:text-gray-600"}`}
              title="Vista Kanban por responsable">
              <LayoutGrid size={15} />
            </button>
          </div>
          {user?.role === "admin" && (
            <>
              <button onClick={() => setShowAiModal(true)}
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-all shadow-sm"
                data-testid="new-checklist-ai-btn"
                title="Generar checklist con IA a partir de una Oferta TIC o Proyecto DevOps">
                <Sparkles size={16} /> Generar con IA
              </button>
              <button onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] transition-colors"
                data-testid="new-checklist-btn">
                <Plus size={16} /> Nuevo Checklist
              </button>
            </>
          )}
        </div>
      </div>

      {viewMode === "kanban" ? (
        <ChecklistKanban />
      ) : (
        <>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            {[["todas", "Todas"], ["tic", "Implantación TIC"], ["arrive", "ARRIVE"]].map(([v, l]) => (
              <button key={v} onClick={() => setTipoTab(v)} data-testid={`checklist-tipo-tab-${v}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tipoTab === v ? "bg-white text-[#007934] shadow-sm" : "text-gray-500"}`}>
                {l}
              </button>
            ))}
          </div>

          {unified.filter(c => tipoTab === "todas" || c.tipo === tipoTab).length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-200">
              <CheckSquare size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-400 text-sm">No hay checklists aún</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {unified.filter(c => tipoTab === "todas" || c.tipo === tipoTab).map(cl => {
              const pct = cl.progress_pct || 0;
              const color = pct === 100 ? "bg-green-500" : pct >= 60 ? "bg-amber-400" : "bg-red-400";
              const isTic = cl.tipo === "tic";
              return (
                <div key={cl.id} onClick={() => isTic ? onSelect(cl.id) : navigate(`/arrive/${cl.id}`)}
                  className="bg-white rounded-xl border border-gray-100 p-5 cursor-pointer hover:shadow-md hover:border-[#007934]/30 transition-all group"
                  data-testid={`checklist-card-${cl.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${isTic ? "bg-blue-100 text-blue-600" : "bg-purple-100 text-purple-600"}`}>
                          {isTic ? "TIC" : "ARRIVE"}
                        </span>
                        {isTic && cl.generated_by_ai && <Sparkles size={11} className="text-purple-500" title="Generado con IA" />}
                      </div>
                      <h3 className="font-bold text-gray-900 font-['Outfit'] group-hover:text-[#007934] transition-colors leading-tight">{cl.titulo}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{cl.subtitulo}{cl.centro_nombre ? ` · ${cl.centro_cb} ${cl.centro_nombre}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-lg font-bold ${pct === 100 ? "text-green-600" : pct >= 60 ? "text-amber-600" : "text-red-500"}`}>{pct}%</span>
                      {isTic && (
                        <>
                          <button onClick={e => handleDuplicate(e, cl.id)}
                            disabled={duplicating === cl.id}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded disabled:opacity-40"
                            data-testid={`duplicate-checklist-${cl.id}`}
                            title="Duplicar checklist">
                            {duplicating === cl.id ? <Loader2 size={14} className="animate-spin"/> : <Copy size={14} />}
                          </button>
                          <button onClick={e => handleDelete(e, cl.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                            data-testid={`delete-checklist-${cl.id}`}
                            title="Eliminar checklist">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                    <div className={`h-2 rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{cl.done_items || 0} / {cl.total_items || 0} items completados</span>
                    <span>{new Date(cl.updated_at || cl.created_at).toLocaleDateString("es-ES")}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showCreate && (
        <NewChecklistModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
      {showAiModal && (
        <GenerateAIModal
          onClose={() => setShowAiModal(false)}
          onCreated={() => { setShowAiModal(false); load(); }}
        />
      )}
    </div>
  );
}

// Iter186 · Modal "Generar con IA": permite elegir Oferta TIC o Proyecto DevOps
// activo y lanza la generación en background. Hace polling a
// /checklists/generate-ai-status/{job_id} hasta ver status=done|error.
function GenerateAIModal({ onClose, onCreated }) {
  const [sources, setSources] = useState({ offers: [], projects: [] });
  const [tipo, setTipo] = useState("devops_project");
  const [selectedId, setSelectedId] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  // Iter255 · Guard contra el doble-fetch de React StrictMode (dev): sin esto
  // el modal dispara la petición 2 veces en cada apertura.
  const fetchedRef = useRef(false);

  const loadSources = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    axios.get(`${API}/checklists-ai-sources`)
      .then(r => setSources(r.data))
      .catch(e => {
        const status = e?.response?.status;
        setLoadError(status || "network");
        toast.error(status ? `No se pudieron cargar las fuentes (HTTP ${status})` : "No se pudieron cargar las fuentes (sin conexión)");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadSources();
  }, [loadSources]);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const t0 = Date.now();
    const tick = async () => {
      if (cancelled) return;
      setElapsed(Math.floor((Date.now() - t0) / 1000));
      try {
        const r = await axios.get(`${API}/checklists/generate-ai-status/${jobId}`);
        setJobStatus(r.data);
        if (r.data.status === "done") {
          toast.success(`Checklist generado: ${r.data.titulo} (${r.data.sections_count} secciones, ${r.data.total_items} ítems)`);
          onCreated();
          return;
        }
        if (r.data.status === "error") {
          toast.error(`Error al generar: ${r.data.error || "sin detalle"}`);
          return;
        }
        setTimeout(tick, 3000);
      } catch (e) {
        toast.error("Error al comprobar el estado del job");
      }
    };
    setTimeout(tick, 2000);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const items = tipo === "offer" ? sources.offers : sources.projects;
  const filtered = items.filter(it => {
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return `${it.title || ""} ${it.municipality || it.unit || it.area || ""} ${it.licitacion || it.azure_devops_project || ""}`.toLowerCase().includes(t);
  }).slice(0, 100);

  const handleGenerate = async () => {
    if (!selectedId) { toast.error("Selecciona una oferta o proyecto"); return; }
    try {
      const r = await axios.post(`${API}/checklists/generate-ai`, {
        source_type: tipo,
        source_id: selectedId,
      });
      setJobId(r.data.job_id);
      setJobStatus({ status: "pending" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al iniciar la generación";
      toast.error(msg);
    }
  };

  const isRunning = jobId && jobStatus?.status === "pending";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="gen-ai-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-purple-50 via-indigo-50 to-white">
          <div>
            <h2 className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
              <Sparkles size={16} className="text-purple-600"/> Generar Checklist con IA
            </h2>
            <p className="text-[11px] text-slate-500">Selecciona la Oferta TIC o el Proyecto DevOps activo. La IA (Claude Sonnet 4.6) genera secciones y ítems adaptados al contexto.</p>
          </div>
          <button onClick={onClose} disabled={isRunning} className="text-slate-400 hover:text-rose-600 p-1 disabled:opacity-50" data-testid="gen-ai-close"><X size={18}/></button>
        </div>

        {isRunning ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <Loader2 size={38} className="animate-spin text-purple-600 mb-3"/>
            <div className="font-semibold text-slate-800">Generando checklist con IA…</div>
            <div className="text-xs text-slate-500 mt-1">Claude está analizando el contexto y produciendo secciones e ítems. Puede tardar entre 30 y 90 segundos.</div>
            <div className="text-[11px] text-slate-400 mt-2 font-mono">Transcurrido: {elapsed}s</div>
          </div>
        ) : loadError ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 gap-2" data-testid="gen-ai-error-state">
            <AlertTriangle size={32} className="text-rose-500"/>
            <div className="font-semibold text-slate-800 text-sm">No se pudieron cargar las fuentes</div>
            <div className="text-xs text-slate-500">{loadError === "network" ? "Sin conexión con el servidor." : `El servidor respondió HTTP ${loadError}.`}</div>
            <button onClick={loadSources} className="mt-2 px-3 py-1.5 text-xs font-semibold rounded-lg bg-purple-600 text-white hover:bg-purple-700" data-testid="gen-ai-retry">
              Reintentar
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-3 overflow-auto flex-1">
            {/* Selector tipo */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setTipo("devops_project"); setSelectedId(""); }}
                data-testid="gen-ai-tipo-devops"
                className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg border ${
                  tipo === "devops_project"
                    ? "bg-indigo-600 text-white border-indigo-700"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}>
                Proyecto DevOps activo ({sources.projects.length})
              </button>
              <button
                type="button"
                onClick={() => { setTipo("offer"); setSelectedId(""); }}
                data-testid="gen-ai-tipo-offer"
                className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg border ${
                  tipo === "offer"
                    ? "bg-emerald-600 text-white border-emerald-700"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}>
                Oferta TIC ({sources.offers.length})
              </button>
            </div>

            {/* Buscador */}
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar por título, municipio, licitación..."
              data-testid="gen-ai-search"
              className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-purple-500"
            />

            {/* Lista */}
            <div className="border border-slate-200 rounded-lg max-h-64 overflow-auto">
              {loading && <div className="p-6 text-center text-xs text-slate-500"><Loader2 size={18} className="animate-spin inline"/></div>}
              {!loading && filtered.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-500">Sin {tipo === "offer" ? "ofertas TIC" : "proyectos DevOps activos"} disponibles.</div>
              )}
              {filtered.map(it => (
                <label
                  key={it.id}
                  className={`flex items-start gap-2 px-3 py-2 border-b border-slate-100 cursor-pointer transition ${
                    selectedId === it.id ? "bg-purple-50" : "hover:bg-slate-50"
                  }`}
                  data-testid={`gen-ai-item-${it.id}`}
                >
                  <input
                    type="radio"
                    name="source"
                    checked={selectedId === it.id}
                    onChange={() => setSelectedId(it.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{it.title || "(sin título)"}</div>
                    <div className="text-[10px] text-slate-500 flex gap-2">
                      <span>{it.municipality || it.unit || it.area || "—"}</span>
                      <span>·</span>
                      <span>{it.licitacion || it.azure_devops_project || "—"}</span>
                      <span>·</span>
                      <span className="uppercase">{it.status}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="px-4 py-3 border-t bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} disabled={isRunning} className="text-xs px-3 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-50">Cancelar</button>
          <button
            onClick={handleGenerate}
            disabled={!selectedId || isRunning}
            data-testid="gen-ai-generate-btn"
            className="text-xs px-4 py-1.5 rounded bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5">
            <Sparkles size={13}/> Generar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail view ────────────────────────────────────────────────────────────
function ChecklistDetail({ id, onBack }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cl, setCl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(null);
  const [editingOwner, setEditingOwner] = useState(null); // item_id being edited
  const [ownerInput, setOwnerInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [naMotivoInput, setNaMotivoInput] = useState({}); // item_id -> texto
  const [blockerForm, setBlockerForm] = useState({ condicion: "", dueño: "", proxima_accion: "", dueño_email: "" });
  const [notifiableUsers, setNotifiableUsers] = useState([]);
  const [showBlockerForm, setShowBlockerForm] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [devolucionMotivo, setDevolucionMotivo] = useState("");
  const [showDevolucionForm, setShowDevolucionForm] = useState(null); // estado destino pendiente de motivo
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [showEnvioForm, setShowEnvioForm] = useState(false);
  const [envioForm, setEnvioForm] = useState({ emisor_nombre: "", receptor_nombre: "", documentacion_disponible: false });
  const [showAccionForm, setShowAccionForm] = useState(false);
  const [accionForm, setAccionForm] = useState({ descripcion: "", responsable_email: "", vencimiento: "", resultado_esperado: "" });
  // T12 (tanda integral 12/10/2026) · plantilla de arranque por líneas +
  // formulario de condiciones documentadas al pasar a "En servicio".
  const [showServicioForm, setShowServicioForm] = useState(false);
  const [servicioCondiciones, setServicioCondiciones] = useState("");
  const [lineas, setLineas] = useState(null);
  const [lineasBusy, setLineasBusy] = useState(null); // linea_id en curso
  const [naMotivoLinea, setNaMotivoLinea] = useState({}); // linea_id -> texto

  useEffect(() => {
    axios.get(`${API}/checklists/${id}`).then(r => {
      setCl(r.data);
      const exp = {};
      (r.data.sections || []).slice(0, 3).forEach(s => { exp[s.id] = true; });
      setExpanded(exp);
    }).finally(() => setLoading(false));
    axios.get(`${API}/horas/users`).then(r => setNotifiableUsers(r.data || [])).catch(() => {});
    axios.get(`${API}/checklists/${id}/lineas-arranque`).then(r => setLineas(r.data.lineas)).catch(() => {});
  }, [id]);

  const decideLinea = async (lineaId, aplica) => {
    const motivo_na = naMotivoLinea[lineaId];
    if (!aplica && !motivo_na) { toast.error("Indica el motivo para marcar esta línea como N/A"); return; }
    setLineasBusy(lineaId);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/lineas-arranque/${lineaId}`, { aplica, motivo_na });
      setLineas(data.lineas);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la decisión");
    } finally { setLineasBusy(null); }
  };

  const toggleLineaTarea = async (lineaId, tareaId) => {
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/lineas-arranque/${lineaId}/tareas/${tareaId}`);
      setLineas(data.lineas);
    } catch (e) { console.error(e); }
  };

  const confirmarEnServicio = async () => {
    if (!servicioCondiciones.trim()) { toast.error("Documenta las condiciones del servicio"); return; }
    await doHandoffTransition("en_servicio", { condiciones_documentadas: servicioCondiciones });
    setShowServicioForm(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/checklists/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `checklist_${id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("Export error:", e); }
    finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/checklists/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `checklist_${id}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("PDF export error:", e); }
    finally { setExportingPdf(false); }
  };

  const updateItem = async (itemId, newStatus, extraFields = {}) => {
    setSaving(itemId);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/items/${itemId}`, { status: newStatus, ...extraFields });
      setCl(data);
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  // R13 (criterios 2026-10-11) · severidad critica/alta/ninguna por item,
  // y solicitud + aprobacion separada de "No aplica" (nunca automatica).
  const SEVERIDAD_CYCLE = { null: "alta", alta: "critica", critica: null };
  const cycleSeveridad = async (item) => {
    const next = SEVERIDAD_CYCLE[item.severidad || "null"];
    setSaving(item.id);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/items/${item.id}`, { status: item.status, severidad: next });
      setCl(data);
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const requestNa = async (item) => {
    const motivo = naMotivoInput[item.id];
    if (!motivo) { toast.error("Indica el motivo para solicitar N/A"); return; }
    setSaving(item.id);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/items/${item.id}`, { status: item.status, na_requested: true, na_motivo: motivo });
      setCl(data);
      toast.success("Solicitud de N/A enviada — pendiente de aprobación");
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const decideNa = async (item, approved) => {
    setSaving(item.id);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/items/${item.id}/na-approval`, { approved, motivo: item.na_motivo || "" });
      setCl(data);
      toast.success(approved ? "N/A aprobada — excluida del total aplicable" : "N/A rechazada — vuelve a la base aplicable");
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const doHandoffTransition = async (nuevo_estado, extra = {}) => {
    setHandoffBusy(true);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/handoff/transition`, { nuevo_estado, ...extra });
      setCl(data);
      toast.success(`Traspaso actualizado a "${HANDOFF_LABELS[nuevo_estado] || nuevo_estado}"`);
      setShowCancelForm(false); setCancelMotivo("");
      setShowDevolucionForm(null); setDevolucionMotivo("");
      setShowServicioForm(false); setServicioCondiciones("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el traspaso");
    } finally { setHandoffBusy(false); }
  };

  const addBlocker = async () => {
    if (!blockerForm.condicion || !blockerForm.dueño) { toast.error("Indica condición y dueño del bloqueo"); return; }
    setHandoffBusy(true);
    try {
      const { data } = await axios.post(`${API}/checklists/${id}/handoff/bloqueos`, blockerForm);
      setCl(data);
      setBlockerForm({ condicion: "", dueño: "", proxima_accion: "", dueño_email: "" });
      setShowBlockerForm(false);
      toast.success("Bloqueo registrado");
    } catch (e) { toast.error("No se pudo registrar el bloqueo"); }
    finally { setHandoffBusy(false); }
  };

  const resolveBlocker = async (bid) => {
    setHandoffBusy(true);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/handoff/bloqueos/${bid}/resolver`);
      setCl(data);
      toast.success("Bloqueo resuelto");
    } catch (e) { toast.error("No se pudo resolver el bloqueo"); }
    finally { setHandoffBusy(false); }
  };

  const saveNextAction = async () => {
    if (!accionForm.descripcion) { toast.error("Indica la descripción de la siguiente acción"); return; }
    setHandoffBusy(true);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/handoff/siguiente-accion`, accionForm);
      setCl(data);
      setShowAccionForm(false);
      toast.success("Siguiente acción guardada");
    } catch (e) { toast.error("No se pudo guardar la siguiente acción"); }
    finally { setHandoffBusy(false); }
  };

  const saveOwner = async (itemId, currentStatus, owner) => {
    setSaving(itemId);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/items/${itemId}`, { status: currentStatus, owner: owner || null });
      setCl(data);
    } catch (e) { console.error(e); }
    finally { setSaving(null); setEditingOwner(null); }
  };

  const updateFecha = async (itemId, currentStatus, fecha) => {
    setSaving(itemId);
    try {
      const { data } = await axios.put(`${API}/checklists/${id}/items/${itemId}`, { status: currentStatus, fecha_compromiso: fecha });
      setCl(data);
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  // Iter313 · O06 — guardar hitos (firma/inicio previsto/puesta en servicio)
  const onUpdateField = async (field, value) => {
    setCl(prev => ({ ...prev, [field]: value }));
    try {
      const { data } = await axios.put(`${API}/checklists/${id}`, { [field]: value || null });
      setCl(data);
    } catch (e) { console.error(e); toast.error("No se pudo guardar la fecha"); }
  };

  if (loading) return <div className="flex items-center justify-center h-48"><Loader2 size={24} className="animate-spin text-[#007934]" /></div>;
  if (!cl) return null;

  const allItems = cl.sections?.flatMap(s => s.items || []) || [];
  const total = allItems.length;
  const done  = allItems.filter(i => i.status === "si").length;
  const pct   = total ? Math.round(done / total * 100) : 0;
  const allOwners = [...new Set(allItems.map(i => i.owner).filter(Boolean))].sort();

  const matchesFilter = (item) => {
    if (filterStatus !== "all" && item.status !== filterStatus) return false;
    if (filterOwner !== "all" && (item.owner || "") !== filterOwner) return false;
    if (search && !item.concepto?.toLowerCase().includes(search.toLowerCase()) &&
        !item.pendiente_de?.toLowerCase().includes(search.toLowerCase()) &&
        !(item.owner || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <button onClick={onBack} aria-label="Volver" data-testid="checklist-back-btn" className="mt-1 text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100">
          <ChevronRight size={18} className="rotate-180" />
        </button>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">{cl.titulo}</h1>
          <p className="text-sm text-gray-500">{cl.municipio} · {cl.contrato}</p>
          {/* Vínculo Demanda-Checklist (tanda integral 13/10/2026) · "no
              duplicar la misma necesidad al pasar de módulo" — si este
              checklist nació de una Petición IT, se ve de dónde viene. */}
          {cl.peticion_ref_id && (
            <button onClick={() => navigate(`/it-peticiones?open=${cl.peticion_ref_id}`)}
              data-testid="checklist-ver-peticion-origen-btn"
              className="text-[11px] text-indigo-500 hover:text-indigo-700 hover:underline mt-0.5 inline-block">
              Originado en Petición IT →
            </button>
          )}
          {/* Iter313 · O06 — 3 hitos distintos (antes mezclados en una sola
              "fecha"): firma del contrato, inicio previsto de implantación
              y puesta en servicio real. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2" data-testid="checklist-milestones">
            {[
              ["fecha_firma_contrato", "Firma contrato"],
              ["fecha_inicio_previsto", "Inicio previsto"],
              ["fecha_inicio_real", "Inicio real"],
              ["fecha_puesta_en_servicio", "Puesta en servicio"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                {label}:
                <input
                  type="date"
                  value={cl[key] || ""}
                  onChange={e => onUpdateField(key, e.target.value)}
                  data-testid={`checklist-milestone-${key}`}
                  className="border border-gray-200 rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:border-[#007934]"
                />
              </label>
            ))}
          </div>
          {/* R13 (criterios 2026-10-11) · numerador, denominador y
              excluidos siempre visibles — la criticidad no cambia el %
              global por sí sola, se muestra aparte. */}
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="checklist-severity-summary">
            <span className="text-[11px] text-gray-500">
              {cl.done_items ?? 0}/{cl.total_items ?? 0} aplicables {cl.excluded_na_count > 0 ? `(+${cl.excluded_na_count} N/A excluidos)` : ""}
            </span>
            {cl.criticos_pendientes > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="checklist-criticos-pendientes">
                {cl.criticos_pendientes} crítico{cl.criticos_pendientes === 1 ? "" : "s"} pendiente{cl.criticos_pendientes === 1 ? "" : "s"}
              </span>
            )}
            {cl.alta_o_critica_pendientes > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600" data-testid="checklist-alta-critica-pendientes">
                {cl.alta_o_critica_pendientes} alta o crítica pendiente{cl.alta_o_critica_pendientes === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-checklist-btn"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-60"
            style={{ background: "#007934" }}
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? "Exportando..." : "Excel"}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={exportingPdf}
            data-testid="export-checklist-pdf-btn"
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-[#007934] text-[#007934] transition-all disabled:opacity-60 hover:bg-[#f0fdf4]"
          >
            {exportingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {exportingPdf ? "Generando PDF..." : "PDF"}
          </button>
        </div>
      </div>

      {/* R12 (criterios 2026-10-11) · estado del traspaso contrato -> implantación */}
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="handoff-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 font-['Outfit']">Traspaso contrato → implantación</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${HANDOFF_COLOR[cl.handoff?.estado] || "bg-gray-100 text-gray-600"}`} data-testid="handoff-estado-badge">
              {HANDOFF_LABELS[cl.handoff?.estado] || "Borrador"}
            </span>
            <span className="text-[11px] text-gray-400">v{cl.handoff?.version || 1}</span>
          </div>
          <div className="flex items-center gap-2">
            {(HANDOFF_NEXT[cl.handoff?.estado || "borrador"] || []).map(next => (
              next === "devuelto_comercial" ? (
                <button key={next} disabled={handoffBusy} data-testid="handoff-devolver-btn"
                  onClick={() => setShowDevolucionForm(next)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60">
                  Devolver a Comercial
                </button>
              ) : next === "pendiente_aceptacion" && cl.handoff?.estado === "borrador" ? (
                <button key={next} disabled={handoffBusy} data-testid="handoff-abrir-envio-btn"
                  onClick={() => setShowEnvioForm(v => !v)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-white disabled:opacity-60" style={{ background: "#007934" }}>
                  Enviar para aceptación
                </button>
              ) : next === "en_servicio" ? (
                <button key={next} disabled={handoffBusy} data-testid="handoff-abrir-servicio-btn"
                  onClick={() => setShowServicioForm(v => !v)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-white disabled:opacity-60" style={{ background: "#007934" }}>
                  Pasar a En servicio
                </button>
              ) : (
                <button key={next} disabled={handoffBusy} data-testid={`handoff-transition-${next}-btn`}
                  onClick={() => doHandoffTransition(next)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg text-white disabled:opacity-60" style={{ background: "#007934" }}>
                  {HANDOFF_LABELS[next]}
                </button>
              )
            ))}
            {!["en_servicio", "cancelado"].includes(cl.handoff?.estado) && (
              <button disabled={handoffBusy} data-testid="handoff-cancelar-btn" onClick={() => setShowCancelForm(true)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-60">
                Cancelar traspaso
              </button>
            )}
          </div>
        </div>

        {showEnvioForm && (
          <div className="flex flex-col gap-2 mb-3 bg-emerald-50 rounded-lg p-3" data-testid="handoff-envio-form">
            <p className="text-[11px] text-gray-500">Para enviar el traspaso deben constar emisor, receptor y confirmar que la documentación está disponible.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={envioForm.emisor_nombre} onChange={e => setEnvioForm(f => ({ ...f, emisor_nombre: e.target.value }))}
                placeholder="Emisor (obligatorio)" data-testid="handoff-envio-emisor-input"
                className="flex-1 min-w-[180px] text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
              <input value={envioForm.receptor_nombre} onChange={e => setEnvioForm(f => ({ ...f, receptor_nombre: e.target.value }))}
                placeholder="Receptor en implantación (obligatorio)" data-testid="handoff-envio-receptor-input"
                className="flex-1 min-w-[180px] text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
              <label className="flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" data-testid="handoff-envio-documentacion-checkbox"
                  checked={envioForm.documentacion_disponible}
                  onChange={e => setEnvioForm(f => ({ ...f, documentacion_disponible: e.target.checked }))} />
                Documentación disponible
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="handoff-envio-confirmar-btn"
                disabled={handoffBusy || !envioForm.emisor_nombre || !envioForm.receptor_nombre || !envioForm.documentacion_disponible}
                onClick={() => { doHandoffTransition("pendiente_aceptacion", envioForm); setShowEnvioForm(false); }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#007934] text-white disabled:opacity-60">
                Confirmar envío
              </button>
              <button onClick={() => setShowEnvioForm(false)} className="text-xs text-gray-400 px-2">Cerrar</button>
            </div>
          </div>
        )}

        {showDevolucionForm && (
          <div className="flex items-center gap-2 mb-3 bg-red-50 rounded-lg p-2">
            <input value={devolucionMotivo} onChange={e => setDevolucionMotivo(e.target.value)}
              placeholder="Motivo de la devolución a Comercial (obligatorio)"
              data-testid="handoff-devolucion-motivo-input"
              className="flex-1 text-xs border border-red-200 rounded px-2 py-1.5 focus:outline-none" />
            <button data-testid="handoff-devolucion-confirmar-btn"
              onClick={() => doHandoffTransition("devuelto_comercial", { motivo: devolucionMotivo })}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white disabled:opacity-60" disabled={!devolucionMotivo || handoffBusy}>
              Confirmar
            </button>
            <button onClick={() => { setShowDevolucionForm(null); setDevolucionMotivo(""); }} className="text-xs text-gray-400 px-2">Cancelar</button>
          </div>
        )}

        {showCancelForm && (
          <div className="flex items-center gap-2 mb-3 bg-gray-50 rounded-lg p-2">
            <input value={cancelMotivo} onChange={e => setCancelMotivo(e.target.value)}
              placeholder="Motivo de la cancelación (obligatorio)"
              data-testid="handoff-cancel-motivo-input"
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
            <button data-testid="handoff-cancel-confirmar-btn"
              onClick={() => doHandoffTransition("cancelado", { motivo: cancelMotivo })}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-700 text-white disabled:opacity-60" disabled={!cancelMotivo || handoffBusy}>
              Confirmar cancelación
            </button>
            <button onClick={() => { setShowCancelForm(false); setCancelMotivo(""); }} className="text-xs text-gray-400 px-2">Cerrar</button>
          </div>
        )}

        {/* T12 · "En servicio exige condiciones documentadas" */}
        {showServicioForm && (
          <div className="flex flex-col gap-2 mb-3 bg-emerald-50 rounded-lg p-3" data-testid="handoff-servicio-form">
            <p className="text-[11px] text-gray-500">
              Describe las condiciones en las que el servicio queda en marcha (qué está operativo, qué queda pendiente de seguimiento).
              {(lineas || []).some(l => l.aplica === null) && (
                <span className="text-red-500 font-medium"> Quedan líneas de arranque sin decidir (ver abajo) — hay que resolverlas antes de confirmar.</span>
              )}
            </p>
            <textarea value={servicioCondiciones} onChange={e => setServicioCondiciones(e.target.value)}
              placeholder="Condiciones documentadas del servicio (obligatorio)" rows={3}
              data-testid="handoff-servicio-condiciones-input"
              className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none resize-none" />
            <div className="flex items-center gap-2">
              <button data-testid="handoff-servicio-confirmar-btn"
                disabled={handoffBusy || !servicioCondiciones.trim()}
                onClick={confirmarEnServicio}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#007934] text-white disabled:opacity-60">
                Confirmar paso a En servicio
              </button>
              <button onClick={() => setShowServicioForm(false)} className="text-xs text-gray-400 px-2">Cerrar</button>
            </div>
          </div>
        )}

        {cl.handoff?.motivo_devolucion && cl.handoff?.estado !== "pendiente_aceptacion" && (
          <p className="text-[11px] text-red-500 mb-2">Última devolución: {cl.handoff.motivo_devolucion}</p>
        )}
        {cl.handoff?.receptor_nombre && (
          <p className="text-[11px] text-gray-500 mb-2">Receptor en implantación: <span className="font-medium text-gray-700">{cl.handoff.receptor_nombre}</span></p>
        )}

        {/* N05 · siguiente acción del traspaso */}
        <div className="mb-3 bg-gray-50 rounded-lg p-2.5" data-testid="handoff-siguiente-accion-panel">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-600">Siguiente acción</span>
            <button onClick={() => { setShowAccionForm(v => !v); if (cl.handoff?.siguiente_accion) setAccionForm({ descripcion: cl.handoff.siguiente_accion.descripcion || "", responsable_email: cl.handoff.siguiente_accion.responsable_email || "", vencimiento: cl.handoff.siguiente_accion.vencimiento || "", resultado_esperado: cl.handoff.siguiente_accion.resultado_esperado || "" }); }}
              data-testid="handoff-editar-accion-btn" className="text-[11px] text-[#007934] font-medium hover:underline">
              {cl.handoff?.siguiente_accion ? "Editar" : "+ Definir"}
            </button>
          </div>
          {!showAccionForm && (
            cl.handoff?.siguiente_accion ? (
              <div className="text-[11px] text-gray-600" data-testid="handoff-siguiente-accion-detalle">
                <p>{cl.handoff.siguiente_accion.descripcion}</p>
                <p className="text-gray-400 mt-0.5">
                  Responsable: {cl.handoff.siguiente_accion.responsable_email || "sin asignar"} ·
                  Vencimiento: {cl.handoff.siguiente_accion.vencimiento || "Pendiente de fijar plazo"}
                </p>
                {cl.handoff.siguiente_accion.resultado_esperado && <p className="text-gray-400 mt-0.5">Resultado esperado: {cl.handoff.siguiente_accion.resultado_esperado}</p>}
              </div>
            ) : (
              <p className="text-[11px] text-gray-400" data-testid="handoff-siguiente-accion-vacia">Sin siguiente acción definida</p>
            )
          )}
          {showAccionForm && (
            <div className="flex flex-col gap-1.5 mt-1.5">
              <input value={accionForm.descripcion} onChange={e => setAccionForm(f => ({ ...f, descripcion: e.target.value }))}
                placeholder="Qué hay que hacer (obligatorio)" data-testid="handoff-accion-descripcion-input"
                className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
              <div className="flex items-center gap-2 flex-wrap">
                <input value={accionForm.responsable_email} onChange={e => setAccionForm(f => ({ ...f, responsable_email: e.target.value }))}
                  placeholder="Responsable (email)" data-testid="handoff-accion-responsable-input"
                  className="flex-1 min-w-[160px] text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
                <input type="date" value={accionForm.vencimiento} onChange={e => setAccionForm(f => ({ ...f, vencimiento: e.target.value }))}
                  data-testid="handoff-accion-vencimiento-input"
                  className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
              </div>
              <input value={accionForm.resultado_esperado} onChange={e => setAccionForm(f => ({ ...f, resultado_esperado: e.target.value }))}
                placeholder="Resultado esperado" data-testid="handoff-accion-resultado-input"
                className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none" />
              <div className="flex items-center gap-2">
                <button onClick={saveNextAction} disabled={handoffBusy} data-testid="handoff-accion-guardar-btn"
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#007934] text-white disabled:opacity-60">Guardar</button>
                <button onClick={() => setShowAccionForm(false)} className="text-xs text-gray-400 px-2">Cerrar</button>
              </div>
            </div>
          )}
        </div>

        {/* Bloqueos */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">Bloqueos</span>
            <button onClick={() => setShowBlockerForm(v => !v)} data-testid="handoff-add-bloqueo-btn"
              className="text-[11px] text-[#007934] font-medium hover:underline">+ Añadir bloqueo</button>
          </div>
          {showBlockerForm && (
            <div className="flex flex-wrap items-center gap-2 mb-2 bg-amber-50 rounded-lg p-2">
              <input value={blockerForm.condicion} onChange={e => setBlockerForm(f => ({ ...f, condicion: e.target.value }))}
                placeholder="Condición bloqueante" data-testid="handoff-bloqueo-condicion-input"
                className="flex-1 min-w-32 text-xs border border-amber-200 rounded px-2 py-1.5 focus:outline-none" />
              <input value={blockerForm.dueño} onChange={e => setBlockerForm(f => ({ ...f, dueño: e.target.value }))}
                placeholder="Dueño" data-testid="handoff-bloqueo-dueno-input"
                className="w-28 text-xs border border-amber-200 rounded px-2 py-1.5 focus:outline-none" />
              <input value={blockerForm.proxima_accion} onChange={e => setBlockerForm(f => ({ ...f, proxima_accion: e.target.value }))}
                placeholder="Próxima acción" data-testid="handoff-bloqueo-accion-input"
                className="w-36 text-xs border border-amber-200 rounded px-2 py-1.5 focus:outline-none" />
              <select value={blockerForm.dueño_email} onChange={e => setBlockerForm(f => ({ ...f, dueño_email: e.target.value }))}
                data-testid="handoff-bloqueo-notificar-select"
                className="text-xs border border-amber-200 rounded px-2 py-1.5 focus:outline-none bg-white">
                <option value="">Sin notificar a nadie</option>
                {notifiableUsers.map(u => <option key={u.email} value={u.email}>{u.name || u.email}</option>)}
              </select>
              <button onClick={addBlocker} disabled={handoffBusy} data-testid="handoff-bloqueo-guardar-btn"
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500 text-white disabled:opacity-60">Guardar</button>
            </div>
          )}
          {(cl.handoff?.bloqueos || []).length === 0 ? (
            <p className="text-[11px] text-gray-400">Sin bloqueos registrados.</p>
          ) : (
            <div className="space-y-1.5">
              {cl.handoff.bloqueos.map(b => (
                <div key={b.id} className={`flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-[11px] ${b.resuelto ? "bg-gray-50 text-gray-400" : "bg-red-50 text-red-700"}`}
                  data-testid={`handoff-bloqueo-${b.id}`}>
                  <span className={b.resuelto ? "line-through" : ""}>
                    <strong>{b.condicion}</strong> — {b.dueño}{b.proxima_accion ? ` · ${b.proxima_accion}` : ""}
                    {b.dueño_email && (
                      <span className="ml-1.5 text-[10px] text-amber-600" data-testid={`handoff-bloqueo-notificado-${b.id}`}>
                        · avisando a {b.dueño_email} cada 24h hasta resolver
                      </span>
                    )}
                  </span>
                  {!b.resuelto && (
                    <button onClick={() => resolveBlocker(b.id)} disabled={handoffBusy} data-testid={`handoff-bloqueo-resolver-${b.id}-btn`}
                      className="text-[10px] font-bold text-green-600 hover:underline whitespace-nowrap">Resolver</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Historial */}
        {cl.handoff?.condiciones_servicio && (
          <div className="mb-2 bg-green-50 rounded-lg p-2.5" data-testid="handoff-condiciones-servicio">
            <p className="text-[11px] font-semibold text-green-700">Condiciones del servicio</p>
            <p className="text-[11px] text-green-800 mt-0.5">{cl.handoff.condiciones_servicio}</p>
            <p className="text-[10px] text-green-500 mt-0.5">
              {cl.handoff.condiciones_servicio_por} · {cl.handoff.condiciones_servicio_fecha ? new Date(cl.handoff.condiciones_servicio_fecha).toLocaleString("es-ES") : ""}
            </p>
          </div>
        )}
        {(cl.handoff?.historial || []).length > 0 && (
          <details className="mt-3">
            <summary className="text-[11px] text-gray-400 cursor-pointer select-none">Historial de transiciones ({cl.handoff.historial.length})</summary>
            <div className="mt-1.5 space-y-1">
              {cl.handoff.historial.slice().reverse().map((h, idx) => (
                <p key={idx} className="text-[11px] text-gray-500">
                  {HANDOFF_LABELS[h.estado_anterior] || h.estado_anterior} → <strong>{HANDOFF_LABELS[h.estado_nuevo] || h.estado_nuevo}</strong>
                  {h.usuario ? ` · ${h.usuario}` : ""} · {h.fecha ? new Date(h.fecha).toLocaleString("es-ES") : ""}
                  {h.motivo ? ` · "${h.motivo}"` : ""}
                </p>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* T12 (tanda integral 12/10/2026) · Plantilla de arranque por líneas.
          Cada línea puede ser N/A justificado — nunca una obligación
          universal de comprar; marcar aplica=true sólo genera tareas de
          proceso (revisar/coordinar/evaluar), ninguna orden de compra real. */}
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="lineas-arranque-panel">
        <div className="flex items-center justify-between mb-1">
          <span className="font-semibold text-gray-900 font-['Outfit']">Plantilla de arranque</span>
          {lineas && (
            <span className="text-[11px] text-gray-400" data-testid="lineas-arranque-resumen">
              {lineas.filter(l => l.aplica === true).length} aplicables · {lineas.filter(l => l.aplica === false).length} N/A · {lineas.filter(l => l.aplica === null).length} sin decidir
            </span>
          )}
        </div>
        <p className="text-[11px] text-gray-400 mb-3">Decide qué líneas de trabajo aplican a este contrato. Marcar una línea como N/A exige un motivo — nunca se asume aplicable ni se descarta por defecto.</p>
        {!lineas ? (
          <p className="text-xs text-gray-400">Cargando...</p>
        ) : (
          <div className="space-y-2">
            {lineas.map(l => (
              <div key={l.id} className="border border-gray-100 rounded-lg p-2.5" data-testid={`linea-arranque-${l.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">{l.label}</span>
                    {l.aplica === true && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`linea-arranque-${l.id}-estado`}>Aplica · {l.responsable_depto}</span>}
                    {l.aplica === true && l.origen === "inferido_peticion" && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600" title="Pre-rellenado automáticamente desde los servicios marcados en la Petición IT — confírmalo o cámbialo">
                        Inferido de la petición · revisar
                      </span>
                    )}
                    {l.aplica === false && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" data-testid={`linea-arranque-${l.id}-estado`}>N/A</span>}
                    {l.aplica === null && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`linea-arranque-${l.id}-estado`}>Sin decidir</span>}
                  </div>
                  {l.aplica === null ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button disabled={lineasBusy === l.id} onClick={() => decideLinea(l.id, true)}
                        data-testid={`linea-arranque-${l.id}-aplica-btn`}
                        className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-[#007934] text-white disabled:opacity-60">Aplica</button>
                      <input value={naMotivoLinea[l.id] || ""} onChange={e => setNaMotivoLinea(f => ({ ...f, [l.id]: e.target.value }))}
                        placeholder="Motivo si es N/A" data-testid={`linea-arranque-${l.id}-motivo-input`}
                        className="text-[11px] border border-gray-200 rounded px-2 py-1 w-40 focus:outline-none" />
                      <button disabled={lineasBusy === l.id} onClick={() => decideLinea(l.id, false)}
                        data-testid={`linea-arranque-${l.id}-na-btn`}
                        className="px-2.5 py-1 text-[11px] font-medium rounded-lg border border-gray-300 text-gray-600 disabled:opacity-60">N/A</button>
                    </div>
                  ) : (
                    <button onClick={() => decideLinea(l.id, !l.aplica)} data-testid={`linea-arranque-${l.id}-cambiar-btn`}
                      className="text-[11px] text-gray-400 hover:text-[#007934] hover:underline">Cambiar</button>
                  )}
                </div>
                {l.aplica === false && l.motivo_na && (
                  <p className="text-[11px] text-gray-400 mt-1">Motivo N/A: {l.motivo_na}</p>
                )}
                {l.aplica === true && l.tareas?.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`linea-arranque-${l.id}-tareas`}>
                    {l.tareas.map(t => (
                      <label key={t.id} className="flex items-center gap-2 text-[11px] text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={t.hecho} onChange={() => toggleLineaTarea(l.id, t.id)}
                          data-testid={`linea-arranque-tarea-${t.id}-checkbox`} />
                        <span className={t.hecho ? "line-through text-gray-400" : ""}>{t.descripcion}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>


      {/* Progress summary */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="font-semibold text-gray-900 font-['Outfit']">Progreso Global</span>
          <span className={`text-2xl font-bold font-['Outfit'] ${pct === 100 ? "text-green-600" : pct >= 60 ? "text-amber-600" : "text-red-500"}`}>{pct}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 mb-4">
          <div className={`h-3 rounded-full transition-all ${pct === 100 ? "bg-green-500" : pct >= 60 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[["Completado", done, "text-green-600"], ["Pendiente", allItems.filter(i=>i.status==="pendiente").length, "text-amber-600"], ["No aplica / No", allItems.filter(i=>i.status==="no").length, "text-red-500"]].map(([l,v,c]) => (
            <div key={l} className="text-center bg-gray-50 rounded-lg py-2">
              <p className={`text-lg font-bold ${c}`}>{v}</p>
              <p className="text-xs text-gray-500">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Per-section mini KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {cl.sections?.map(sec => {
          const spct = sec.progress_pct || 0;
          return (
            <div key={sec.id} className="bg-white rounded-lg border border-gray-100 p-3 cursor-pointer hover:border-[#007934]/40"
              onClick={() => setExpanded(prev => ({ ...prev, [sec.id]: !prev[sec.id] }))}>
              <p className="text-xs font-semibold text-gray-500 mb-1 truncate">{sec.capitulo}</p>
              <p className="text-xs text-gray-700 font-medium mb-2 leading-tight line-clamp-2">{sec.nombre}</p>
              <div className="flex items-center gap-1.5">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${spct===100?"bg-green-500":spct>=50?"bg-amber-400":"bg-red-400"}`} style={{width:`${spct}%`}} />
                </div>
                <span className="text-[10px] font-bold text-gray-600">{spct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar concepto, pendiente de, responsable…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]" />
        </div>
        <div className="flex items-center gap-1">
          {[["all","Todos"],["si","Sí"],["pendiente","Pendiente"],["no","No"]].map(([v,l]) => (
            <button key={v} onClick={() => setFilterStatus(v)} data-testid={`filter-status-${v}`}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all ${filterStatus===v ? "bg-[#007934] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-[#007934]"}`}>
              {l}
            </button>
          ))}
        </div>
        {allOwners.length > 0 && (
          <div className="flex items-center gap-1.5">
            <User size={13} className="text-gray-400" />
            <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}
              data-testid="filter-owner-select"
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-[#007934] bg-white">
              <option value="all">Todos los responsables</option>
              <option value="">Sin responsable</option>
              {allOwners.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {cl.sections?.map(sec => {
          const filtered = (sec.items || []).filter(matchesFilter);
          if (filtered.length === 0 && (search || filterStatus !== "all")) return null;
          const isOpen = expanded[sec.id] !== false;
          return (
            <div key={sec.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpanded(prev => ({ ...prev, [sec.id]: !isOpen }))}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {isOpen ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />}
                  <span className="text-xs font-bold text-[#007934] flex-shrink-0">{sec.capitulo}</span>
                  <span className="font-semibold text-gray-900 text-sm truncate">{sec.nombre}</span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                  <span className="text-xs text-gray-500">{sec.done_items}/{sec.total_items}</span>
                  <div className="w-20 bg-gray-100 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${sec.progress_pct===100?"bg-green-500":sec.progress_pct>=50?"bg-amber-400":"bg-red-400"}`} style={{width:`${sec.progress_pct||0}%`}} />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-8 text-right">{sec.progress_pct||0}%</span>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-gray-50">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase tracking-wider">
                        <th className="text-left px-4 py-2 font-medium">Concepto</th>
                        <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">Contenido Mínimo</th>
                        <th className="text-center px-3 py-2 font-medium w-28">Estado</th>
                        <th className="text-left px-3 py-2 font-medium hidden md:table-cell w-32">Responsable</th>
                        <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Pendiente de</th>
                        <th className="text-left px-3 py-2 font-medium hidden xl:table-cell w-28">F. Compromiso</th>
                        <th className="text-left px-3 py-2 font-medium hidden xl:table-cell">Observaciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filtered.map(item => {
                        const cfg = STATUS_CFG[item.status] || STATUS_CFG.pendiente;
                        const Icon = cfg.icon;
                        const isEditingOwner = editingOwner === item.id;
                        return (
                          <tr key={item.id} className="hover:bg-gray-50/60 group" data-testid={`checklist-item-${item.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-2">
                                <Icon size={13} className={`mt-0.5 flex-shrink-0 ${item.status==="si"?"text-green-500":item.status==="no"?"text-red-400":"text-amber-400"}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-gray-900 leading-snug font-medium">{item.concepto}</span>
                                    <button
                                      onClick={() => cycleSeveridad(item)}
                                      disabled={saving === item.id}
                                      data-testid={`item-severidad-${item.id}`}
                                      title="Click para cambiar: ninguna → alta → crítica → ninguna"
                                      className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border shrink-0 ${
                                        item.severidad === "critica" ? "bg-red-100 text-red-700 border-red-200"
                                        : item.severidad === "alta" ? "bg-orange-50 text-orange-600 border-orange-200"
                                        : "bg-gray-50 text-gray-300 border-gray-200"}`}>
                                      {item.severidad === "critica" ? "Crítica" : item.severidad === "alta" ? "Alta" : "+ severidad"}
                                    </button>
                                    {item.na_approved === true && (
                                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid={`item-na-approved-${item.id}`}>
                                        N/A aprobada
                                      </span>
                                    )}
                                  </div>
                                  {item.na_requested && item.na_approved !== true && (
                                    <div className="mt-1 flex items-center gap-1.5 text-[10px]" data-testid={`item-na-pending-${item.id}`}>
                                      <span className="text-amber-600">N/A pendiente de aprobación: &quot;{item.na_motivo}&quot;</span>
                                      {user?.role === "admin" && (
                                        <>
                                          <button onClick={() => decideNa(item, true)} className="text-emerald-600 hover:underline" data-testid={`approve-na-${item.id}`}>Aprobar</button>
                                          <button onClick={() => decideNa(item, false)} className="text-red-500 hover:underline" data-testid={`reject-na-${item.id}`}>Rechazar</button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {!item.na_requested && item.na_approved !== true && (
                                    <div className="mt-1 flex items-center gap-1 text-[10px]">
                                      <input
                                        value={naMotivoInput[item.id] || ""}
                                        onChange={e => setNaMotivoInput(s => ({ ...s, [item.id]: e.target.value }))}
                                        placeholder="Motivo para pedir N/A…"
                                        className="border border-gray-200 rounded px-1 py-0.5 text-[10px] w-32 focus:outline-none focus:border-[#007934]"
                                        data-testid={`na-motivo-input-${item.id}`} />
                                      <button onClick={() => requestNa(item)} className="text-gray-400 hover:text-[#007934]" data-testid={`request-na-${item.id}`}>pedir N/A</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-gray-500 hidden lg:table-cell max-w-xs">
                              <span className="line-clamp-2">{item.contenido_minimo || "—"}</span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <div className="flex gap-1 justify-center">
                                {[["si","Sí","hover:bg-green-500 hover:text-white"],["pendiente","Pte","hover:bg-amber-400 hover:text-white"],["no","No","hover:bg-red-400 hover:text-white"]].map(([v,l,hcls]) => (
                                  <button key={v} onClick={() => updateItem(item.id, v)} disabled={saving === item.id}
                                    className={`px-2 py-0.5 rounded text-[10px] font-bold border transition-all ${item.status===v
                                      ? v==="si"?"bg-green-500 text-white border-green-500"
                                      : v==="no"?"bg-red-400 text-white border-red-400"
                                      : "bg-amber-400 text-white border-amber-400"
                                      : `border-gray-200 text-gray-400 ${hcls}`}`}>
                                    {saving === item.id ? "…" : l}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-3 hidden md:table-cell w-32">
                              {isEditingOwner ? (
                                <div className="flex items-center gap-1">
                                  <input autoFocus value={ownerInput} onChange={e => setOwnerInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") saveOwner(item.id, item.status, ownerInput); if (e.key === "Escape") setEditingOwner(null); }}
                                    className="flex-1 border border-[#007934] rounded px-1 py-0.5 text-[10px] focus:outline-none min-w-0"
                                    placeholder="Responsable…" data-testid={`owner-input-${item.id}`} />
                                  <button onClick={() => saveOwner(item.id, item.status, ownerInput)} className="text-green-600 hover:text-green-700 flex-shrink-0"><CheckCircle size={12}/></button>
                                  <button onClick={() => setEditingOwner(null)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X size={12}/></button>
                                </div>
                              ) : (
                                <button onClick={() => { setEditingOwner(item.id); setOwnerInput(item.owner || ""); }}
                                  data-testid={`owner-cell-${item.id}`}
                                  className="flex items-center gap-1 w-full text-left group/owner">
                                  {item.owner ? (
                                    <span className="bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1">
                                      <User size={9}/>{item.owner}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300 text-[10px] group-hover/owner:text-[#007934]">+ asignar</span>
                                  )}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-3 text-gray-500 hidden md:table-cell">
                              {item.pendiente_de ? (
                                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-medium">{item.pendiente_de}</span>
                              ) : "—"}
                            </td>
                            <td className="px-3 py-2 hidden xl:table-cell w-28">
                              <input type="date" defaultValue={item.fecha_compromiso || ""}
                                onBlur={e => { if (e.target.value !== (item.fecha_compromiso || "")) updateFecha(item.id, item.status, e.target.value || null); }}
                                className="w-full border border-gray-200 rounded px-1 py-0.5 text-[10px] text-gray-600 focus:outline-none focus:border-[#007934] bg-transparent"
                                data-testid={`fecha-compromiso-${item.id}`} />
                            </td>
                            <td className="px-3 py-3 text-gray-400 hidden xl:table-cell max-w-xs">
                              <span className="line-clamp-1">{item.observaciones || "—"}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function Checklists() {
  // R06 (auditoría externa 09/10/2026, E01) · "Mi trabajo" enlazaba siempre
  // al listado general en vez de a la ficha seleccionada (ej. Santa Eulàlia
  // abría /checklists y había que volver a buscarla). Ahora, si se llega
  // con ?open={id} (desde Mi trabajo), se abre el detalle directamente y
  // "Volver" regresa a la bandeja en vez de al listado general.
  // T10 (tanda integral 12/10/2026) · "página completa con URL compartible
  // para trabajo" — el checklist abierto vive en la URL (?open=id), no sólo
  // en estado interno: recargar o compartir el enlace reabre la MISMA
  // ficha. `deepLinkFromMiTrabajo` fija el origen SOLO en el montaje
  // inicial, así "Volver" sigue yendo a Mi Trabajo únicamente si se llegó
  // desde allí (no si se navegó a otra ficha después, dentro de Checklists).
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkFromMiTrabajo = useRef(!!searchParams.get("open")).current;
  const selectedId = searchParams.get("open");
  const selectId = (newId) => setSearchParams(newId ? { open: newId } : {});
  return selectedId
    ? <ChecklistDetail id={selectedId} onBack={() => deepLinkFromMiTrabajo ? navigate("/mi-trabajo") : selectId(null)} />
    : <ChecklistList onSelect={selectId} />;
}
